/**
 * MarkdownRenderer - Markdownコンテンツ
 *
 * 子供安全性のため、rehypeSanitizeを使用してXSS攻撃を防止
 */

import type { ReactNode } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import { Box, BoxProps } from '@chakra-ui/react';

interface MarkdownRendererProps extends BoxProps {
  content: string;
}

// eslint-disable-next-line no-irregular-whitespace
const TRAILING_PATTERN = /[　-〿぀-ゟ゠-ヿ一-鿿＀-￯]+$/;

const sanitizeAutolink = (
  href: string | undefined,
  children: ReactNode,
): { href: string | undefined; children: ReactNode; trailing: string } => {
  if (!href) return { href, children, trailing: '' };
  let decoded: string;
  try {
    decoded = decodeURI(href);
  } catch {
    return { href, children, trailing: '' };
  }
  const match = decoded.match(TRAILING_PATTERN);
  if (!match) return { href, children, trailing: '' };
  const trailing = match[0];
  const cleanedDecoded = decoded.slice(0, -trailing.length);
  let cleanedHref: string;
  try {
    cleanedHref = encodeURI(cleanedDecoded);
  } catch {
    return { href, children, trailing: '' };
  }
  let cleanedChildren: ReactNode = children;
  if (typeof children === 'string' && children.endsWith(trailing)) {
    cleanedChildren = children.slice(0, -trailing.length);
  } else if (Array.isArray(children)) {
    const last = children[children.length - 1];
    if (typeof last === 'string' && last.endsWith(trailing)) {
      cleanedChildren = [...children.slice(0, -1), last.slice(0, -trailing.length)];
    }
  }
  return { href: cleanedHref, children: cleanedChildren, trailing };
};

export const MarkdownRenderer = ({ content, ...props }: MarkdownRendererProps) => {
  return (
    <Box
      m={0}
      maxW="none"
      wordBreak="break-word"
      fontSize="lg"
      fontWeight="normal"
      lineHeight="tall"
      color="gray.700"
      css={{
        '& h1, & h2, & h3, & h4, & h5, & h6': {
          mt: 6,
          mb: 2,
          fontWeight: 'bold',
          lineHeight: 'tight',
          color: 'gray.700',
        },
        '& h1': { fontSize: '2xl', borderBottom: '2px solid', borderColor: 'gray.300', pb: 1 },
        '& h2': { fontSize: 'xl', borderBottom: '1px solid', borderColor: 'gray.300', pb: 1 },
        '& h3': { fontSize: 'lg' },
        '& h4': { fontSize: 'base' },
        '& p': { my: 2 },
        '& p:first-of-type': { mt: 0 },
        '& p:last-of-type': { mb: 0 },
        '& a': {
          color: 'blue.500',
          textDecoration: 'none',
          _hover: { color: 'blue.600', textDecoration: 'underline' },
        },
        '& ul, & ol': { my: 3, pl: 8 },
        '& li': { my: 1 },
        '& li p': { my: 1 },
        '& code': {
          bg: 'gray.100',
          px: 1.5,
          py: 0.5,
          borderRadius: 'sm',
          fontSize: 'sm',
          fontFamily: 'mono',
          color: 'gray.800',
        },
        '& pre': {
          bg: 'gray.800',
          color: 'gray.50',
          p: 4,
          borderRadius: 'lg',
          overflowX: 'auto',
          my: 4,
          boxShadow: 'sm',
          whiteSpace: 'pre',
        },
        '& pre code': {
          bg: 'transparent',
          p: 0,
          color: 'inherit',
          fontSize: 'sm',
          lineHeight: 'relaxed',
        },
        '& blockquote': {
          borderLeft: '4px solid',
          borderColor: 'primary',
          px: 4,
          py: 2,
          my: 4,
          bg: 'blue.50',
          color: 'gray.600',
          fontStyle: 'italic',
        },
        '& blockquote p': { my: 2 },
        '& table': {
          borderCollapse: 'collapse',
          w: 'full',
          minW: 'max-content',
        },
        '& thead': { bg: 'gray.100' },
        '& th, & td': {
          border: '1px solid',
          borderColor: 'gray.300',
          px: 4,
          py: 3,
          whiteSpace: 'normal',
          overflowWrap: 'break-word',
          verticalAlign: 'top',
        },
        '& th': {
          textAlign: 'left',
          fontWeight: 'bold',
          color: 'gray.800',
        },
        '& td': { color: 'gray.700' },
        '& hr': { borderWidth: 0, borderTop: '2px solid', borderColor: 'gray.300', my: 8 },
        '& img': { maxW: 'full', h: 'auto', borderRadius: 'lg', my: 4, boxShadow: 'sm' },
        '& strong': { fontWeight: 'bold', color: 'gray.800' },
        '& em': { fontStyle: 'italic' },
        '& input[type="checkbox"]': { mr: 2, ml: -6 },
        '& del': { textDecoration: 'line-through', color: 'gray.600' },
      }}
      {...props}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeSanitize]}
        components={{
          a: ({ node: _node, href, children, ...anchorProps }) => {
            const {
              href: cleanedHref,
              children: cleanedChildren,
              trailing,
            } = sanitizeAutolink(href, children);
            return (
              <>
                <a
                  {...anchorProps}
                  href={cleanedHref}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {cleanedChildren}
                </a>
                {trailing}
              </>
            );
          },
          table: ({ node: _node, ...tableProps }) => (
            <Box
              my={4}
              maxW="100%"
              overflowX="auto"
              borderRadius="lg"
              boxShadow="sm"
              border="1px solid"
              borderColor="gray.300"
              tabIndex={0}
              role="region"
              aria-label="スクロール可能な表"
              _focusVisible={{
                outline: '2px solid',
                outlineColor: 'brand.primary',
                outlineOffset: '2px',
              }}
            >
              <Box as="table" {...tableProps} />
            </Box>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </Box>
  );
};
